// ContainerDetailModal - zeigt die einzelnen Fächer eines Container-Schranks
// in einer Detail-Ansicht. Klick auf Fach → AssignmentDialog (wenn erlaubt).
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Lock, Loader2 } from 'lucide-react';
import { schrankApi } from '../../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { SchrankGroupRenderer } from './SchrankWall';
import { SchrankStatusBadge } from './SchrankDoor';
import { AssignmentDialog } from './AssignmentDialog';

export function ContainerDetailModal({
  container,
  open,
  onOpenChange,
  canAssign,
  onChanged,
  buildingName,
  floorName,
  roomName,
}) {
  const [faecher, setFaecher] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assignDialog, setAssignDialog] = useState({ open: false, schrank: null });
  const [hoverInfo, setHoverInfo] = useState(null);
  const stageRef = useRef(null);
  const hoverTimerRef = useRef(null);

  useEffect(() => {
    if (!open || !container?.id) return;
    setIsLoading(true);
    schrankApi.listSchraenke({ container_id: container.id })
      .then(res => setFaecher(res.data))
      .catch(() => setFaecher([]))
      .finally(() => setIsLoading(false));
  }, [open, container?.id]);

  // Hover beim Schließen zurücksetzen
  useEffect(() => {
    if (!open) setHoverInfo(null);
  }, [open]);
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const handleHoverEnter = useCallback((schrank, e) => {
    if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHoverInfo({
      schrank,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);
  const handleHoverLeave = useCallback(() => {
    hoverTimerRef.current = setTimeout(() => setHoverInfo(null), 80);
  }, []);

  const reload = () => {
    if (!container?.id) return;
    schrankApi.listSchraenke({ container_id: container.id })
      .then(res => setFaecher(res.data));
    onChanged?.();
  };

  const stats = useMemo(() => {
    const c = { FREI: 0, BELEGT: 0, DEAKTIVIERT: 0, RESERVIERT: 0 };
    faecher.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [faecher]);

  // Layout im Modal: synthetische, quadratische Fächer mit fixem Abstand.
  // Die echten Geometrie-Daten aus der DB sind oft Hochformat/Querformat
  // (abhängig von Container-Größe im Raum); im Modal soll es einheitlich
  // quadratisch und klar sortiert wirken.
  const SIZE = 60;
  const SPACING = 4;
  const PADDING = 8;
  const { displayFaecher, viewBox } = useMemo(() => {
    if (faecher.length === 0) return { displayFaecher: [], viewBox: '0 0 100 100' };
    const spalten = container?.geometry?.spalten || Math.ceil(Math.sqrt(faecher.length));
    // sortiert nach Original-Position (Reihe für Reihe, links nach rechts)
    const sorted = [...faecher].sort((a, b) => {
      const ay = a.geometry?.y || 0, by = b.geometry?.y || 0;
      if (Math.abs(ay - by) > 5) return ay - by;
      return (a.geometry?.x || 0) - (b.geometry?.x || 0);
    });
    const display = sorted.map((s, i) => {
      const col = i % spalten;
      const row = Math.floor(i / spalten);
      return {
        ...s,
        geometry: {
          ...(s.geometry || {}),
          x: PADDING + col * (SIZE + SPACING),
          y: PADDING + row * (SIZE + SPACING),
          width: SIZE,
          height: SIZE,
          rotation: 0,
        },
      };
    });
    const reihen = Math.ceil(faecher.length / spalten);
    const vbW = PADDING * 2 + spalten * SIZE + (spalten - 1) * SPACING;
    const vbH = PADDING * 2 + reihen * SIZE + (reihen - 1) * SPACING;
    return { displayFaecher: display, viewBox: `0 0 ${vbW} ${vbH}` };
  }, [faecher, container?.geometry?.spalten]);

  if (!container) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[97vw] w-[97vw] sm:max-w-[95vw] h-[95vh] max-h-[95vh] overflow-hidden flex flex-col p-4 sm:p-5"
          data-testid="container-detail-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Lock className="w-5 h-5" />
              {container.name}
              <Badge variant="secondary">{faecher.length} Fächer</Badge>
              {container.geometry?.spalten && container.geometry?.reihen && (
                <Badge variant="outline" className="text-xs">
                  {container.geometry.spalten} × {container.geometry.reihen}
                </Badge>
              )}
            </DialogTitle>
            {(buildingName || floorName || roomName) && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap"
                 data-testid="container-modal-breadcrumb">
                {buildingName && <span>{buildingName}</span>}
                {buildingName && (floorName || roomName) && <span>›</span>}
                {floorName && <span>{floorName}</span>}
                {floorName && roomName && <span>›</span>}
                {roomName && <span className="font-medium text-foreground/80">{roomName}</span>}
              </p>
            )}
            <DialogDescription className="flex flex-wrap items-center gap-3 pt-1">
              {Object.keys(stats).map(s => (
                <SchrankStatusBadge key={s} status={s} count={stats[s]} />
              ))}
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <Skeleton className="w-full h-96" />
          ) : faecher.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Dieser Container enthält noch keine Fächer.
            </p>
          ) : (
            <div
              ref={stageRef}
              className="relative flex-1 min-h-0 bg-gradient-to-b from-slate-200 to-slate-300 dark:from-slate-800 dark:to-slate-900 rounded-lg overflow-hidden"
            >
              <svg
                viewBox={viewBox}
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
                style={{ display: 'block' }}
              >
                <SchrankGroupRenderer
                  schraenke={displayFaecher}
                  onSchrankClick={(s) => {
                    // Echten Schrank aus faecher (mit Original-Geometry) finden
                    const real = faecher.find(f => f.id === s.id) || s;
                    if (canAssign) {
                      setAssignDialog({ open: true, schrank: real });
                    }
                  }}
                  onSchrankHoverEnter={handleHoverEnter}
                  onSchrankHoverLeave={handleHoverLeave}
                />
              </svg>

              {/* Hover-Tooltip: zeigt Fach-Info sofort beim Drüberfahren */}
              {hoverInfo && (
                <div
                  data-testid="container-hover-tooltip"
                  className="pointer-events-none absolute z-30 rounded-md border bg-white dark:bg-slate-900 shadow-lg px-3 py-2 text-xs min-w-[160px] max-w-[240px]"
                  style={{
                    left: Math.min(hoverInfo.x + 14, (stageRef.current?.clientWidth || 9999) - 250),
                    top: Math.max(hoverInfo.y - 8, 0),
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor:
                        hoverInfo.schrank.status === 'FREI' ? '#10b981'
                        : hoverInfo.schrank.status === 'BELEGT' ? '#f97316'
                        : hoverInfo.schrank.status === 'RESERVIERT' ? '#eab308'
                        : '#94a3b8' }}
                    />
                    <span className="font-semibold">{hoverInfo.schrank.name}</span>
                  </div>
                  <div className="text-muted-foreground">
                    {hoverInfo.schrank.status === 'FREI' && 'Frei'}
                    {hoverInfo.schrank.status === 'BELEGT' && 'Belegt'}
                    {hoverInfo.schrank.status === 'DEAKTIVIERT' && 'Deaktiviert'}
                    {hoverInfo.schrank.status === 'RESERVIERT' && 'Reserviert'}
                  </div>
                  {hoverInfo.schrank.mitarbeiter_name && (
                    <div className="mt-1 font-medium text-slate-800 dark:text-slate-100">
                      {hoverInfo.schrank.mitarbeiter_name}
                    </div>
                  )}
                  {!hoverInfo.schrank.mitarbeiter_name && hoverInfo.schrank.abteilung_name && (
                    <div className="mt-1 font-medium text-slate-800 dark:text-slate-100">
                      {hoverInfo.schrank.abteilung_name}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!canAssign && faecher.length > 0 && (
            <p className="text-xs text-muted-foreground text-center">
              Klicke auf ein Fach für Detail-Infos. Zuweisungen können nur berechtigte Rollen vornehmen.
            </p>
          )}
        </DialogContent>
      </Dialog>

      <AssignmentDialog
        open={assignDialog.open}
        onOpenChange={(open) => setAssignDialog((s) => ({ ...s, open }))}
        schrank={assignDialog.schrank}
        canManage={canAssign}
        onChanged={reload}
      />
    </>
  );
}

export default ContainerDetailModal;
